import {Listing} from './listing';
import { environment } from '../../environments/environment-wrapper';
import { format } from 'libphonenumber-js';
import {AgentAward, CombinedAwards} from "./interfaces";
import {OAAgentProvider, IndividualAgentAwards} from "../app-config.constants";

export class Agent {
  additionalWeb: string;
  associatePhoto: string;
  awards: Array<AgentAward>;
  boards: [
    {
      boardId: string;
      boardName: string;
    }
  ];

  // Use this to group together awards by continuous years, and to combine career awards with yearly awards
  careerAwards: Array<AgentAward>;
  teamAwards: Array<AgentAward>;
  combinedAwards: Array<CombinedAwards> = new Array<CombinedAwards>();

  civicActivities: string[] = [];
  companyEmail: string;
  companyID: string;
  designations: string[] = [];
  email: string;
  facebook: string;
  fax: string;
  firstName: string;
  gender: string;
  hobbies: string[] = [];
  initialsMiddleName: string;
  languages: string[];
  isFavourite: boolean = false;
  lastName: string;
  linkedIn: string;
  listings: Array<Listing> = [];
  listingWeb: string;
  // TODO: Deprecated
  mobileNumber: string;
  officeId: string;
  officeName: string;
  city: string;
  stateShort: string;
  pager: string;
  personalSummary: string;

  // Use this as the agent ID
  personifyId: string;
  preferredName: string;
  propertyTypes: string[];

  primaryOfficeName: string = ''; // TOOD: what is this
  primaryOfficeNumber: string = ''; // TOOD: what is this

  sourceSystemId: string;
  sourceSystem: string; // HMS or iFranchise. Used in lead creation.
  remaxOfficeId: number;
  remaxTeamId: string;
  serviceAreas: string[] = [];
  specialties: string[] = [];
  status: string;
  suffix: string;
  team: Array<Agent> = [];
  telephone: string;
  title: string;
  tollFree: string;
  twitter: string;
  web: string;
  yearLicensed: string;
  hideAwards: boolean;
  detailUrl: string;

  // Used just for front end
  fullName: string;
  hasSocialMedia: boolean = false;
  parsedPhoneNumber: string;
  // This variable shows agent's incorporation status
  agentIncorporation: string;
  // Agent PREC title to show on UI
  agentPrecTitle: String;
  hasInformationForBio: boolean = false;

  // Agent Initials
  agentInitials: string;

  // The number we want to use when users are able to contact agents
  primaryNumberToContact: string;
  // RFC3966 formatted phone number https://www.w3.org/TR/mwabp/#bp-interaction-uri-schemes
  rfc3966FormattedPhoneNumber: string;

  jsonLdSchema: object[] = [];

  postalCode: string;


  private ICON_CLUB_PLATINUM                    = 'Platinum_Web.png';
  private ICON_CLUB_CHAIRMAN                    = 'Chairmans_Web.jpg';
  private ICON_CLUB_DIAMOND                     = 'Diamond_Web.jpg';
  private ICON_CLUB_TITAN                       = 'Titan_Web.jpg';
  private ICON_CLUB_EXECUTIVE                   = 'Executive_Web.jpg';
  private ICON_CLUB_100_PERCENT                 = '100_Percent_Web.jpg';

  private ICON_TEAM_PLATINUM                    = 'Platinum_TEAM_Web.jpg';
  private ICON_TEAM_CHAIRMAN                    = 'Chairmans_TEAM_Web.jpg';
  private ICON_TEAM_DIAMOND                     = 'Diamond_TEAM_Web.jpg';
  private ICON_TEAM_TITAN                       = 'Titan_TEAM_Web.jpg';
  private ICON_TEAM_EXECUTIVE                   = 'Executive_TEAM_Web.jpg';
  private ICON_TEAM_100_PERCENT                 = '100_Percent_TEAM_Web.jpg';

  private ICON_CAREER_2017_CIRCLE_LEGENDS       = '2017_RM_Circle_Legends_outlines.png';
  private ICON_CAREER_2017_HALL_OF_FAME         = '2017_RM_HallofFame_outlines.png';
  private ICON_CAREER_2017_LIFETIME_ACHIEVEMENT = '2017_RM_Lifetime_Achievement_outlines.png';
  private ICON_CAREER_2017_LUMINARY_DISTINCTION = '2017_RM_Luminary_Distinction_outlines.png';

  constructor(agentStub?: any, favouriteAgents?: string[]) {
    if (agentStub) {
      Object.keys(agentStub).forEach((key) => {
        this[key] = agentStub[key];
      });

      // constructor values here
      this.fullName = `${this.firstName ? this.firstName : ''} ${this.lastName ? this.lastName : ''}`;
      this.hasSocialMedia = (this.web &&  this.web.length > 0) || (this.facebook && this.facebook.length > 0) || (this.twitter && this.twitter.length > 0);
      this._getAgentAwards();
      this.hasInformationForBio = this.personalSummary && this.personalSummary.length > 0
      || (this.serviceAreas && this.serviceAreas.length > 0)
      || (this.specialties && this.specialties.length > 0)
      || (this.hobbies && this.hobbies.length > 0)
      || (this.civicActivities && this.civicActivities.length > 0);

      this.primaryNumberToContact = this.mobileNumber || this.telephone || this.tollFree;
      this.parsedPhoneNumber = this.primaryNumberToContact ? format(this.primaryNumberToContact, 'CA', 'National') : '';
      this.rfc3966FormattedPhoneNumber = this.primaryNumberToContact ? format(this.primaryNumberToContact, 'CA', 'RFC3966') : '';

      this.agentInitials = this.fullName.split(' ').map((name) => name[0]).join('');
      // If they have more than 3 initials, just get the first and last
      if (this.agentInitials.length > 2) {
        this.agentInitials = this.agentInitials[0] + this.agentInitials[this.agentInitials.length - 1];
      }
    }

    this.isFavourite = favouriteAgents && favouriteAgents.indexOf(this.sourceSystemId) >= 0;

    // TODO: Translation
    this.agentPrecTitle = this.agentIncorporation === 'Personal Real Estate Corporation' ? ' - ' + this.agentIncorporation : '';

    // TODO :remove later after backend remove domain
    const officeProfileUrlRegex = /https:\/\/www\.remax\.ca/g;
    this.detailUrl = this.detailUrl ? '/' + this.detailUrl.replace(officeProfileUrlRegex, '') : '';

    this.jsonLdSchema = [{
      '@context': 'http://schema.org',
      '@graph': [
        {
          '@type': 'Person',
          name: this.fullName,
          url: this.detailUrl,
          image: this.associatePhoto,
        },
        {
          '@type': 'RealEstateAgent',
          name: this.fullName,
          url: this.detailUrl,
          image: this.associatePhoto,
          photo: {
            '@type': 'ImageObject',
            url: this.associatePhoto,
            name: this.fullName
          },
          telephone: this.parsedPhoneNumber,
        }
      ]
    }];
  }

  private _getAgentAwards() {
    // Set Career Awards, Team Awards and Yearly Awards with their proper S3 URLs
    let combinedCareerAwards: Array<AgentAward>;
    let combinedTeamAwards: Array<AgentAward>;
    let combinedIndividualAwards: Array<AgentAward>;

    if(this.hideAwards) return; // If hide awards flag is set, hide all awards

    if (this.careerAwards && this.careerAwards.length > 0) {
      combinedCareerAwards = this.careerAwards.map(award =>
        ({url: this._getAwardIcon(award, false, false),
          name: award.name,
          years: [],
          year: null}));

      this.combinedAwards.push({
        awardCategory: 'agent-profile.awards.career',
        awards: combinedCareerAwards
      })
    }

    // team awards are fetched from Personify for both regions
    // show team awards for WC only (as Personify does not have accurate OA data)
    if (this.sourceSystem != 'iFranchise-OA' && this.teamAwards && this.teamAwards.length > 0) {
      combinedTeamAwards = this._groupSameAwards(this.teamAwards, false, true);
      this.combinedAwards.push({
        awardCategory: 'agent-profile.awards.team',
        awards: combinedTeamAwards})
    }

    if (this.awards && this.awards.length > 0) {
      combinedIndividualAwards = this._groupSameAwards(this.awards, true, false)
      this.combinedAwards.push({
        awardCategory: IndividualAgentAwards,
        awards: combinedIndividualAwards
      })
    }
  }

  // Sets the static icon URL for agent awards if they exist
  // Sets it to the S3 Bucket set up for awards
  private _getAwardIcon(award: {name: string, year?: number}, isYearlyAward: boolean, isTeamAward: boolean) {
    let fileName = '';
    if (isYearlyAward) {
      if (award.name === 'Chairman\'s Club') fileName = this.ICON_CLUB_CHAIRMAN;
      else if (award.name === 'Platinum Club' || award.name === 'Platinum Club Team') fileName = this.ICON_CLUB_PLATINUM;
      else if (award.name === 'Titan Club') fileName = this.ICON_CLUB_TITAN;
      else if (award.name === '100% Club') fileName = this.ICON_CLUB_100_PERCENT;
      else if (award.name === 'Executive Club') fileName = this.ICON_CLUB_EXECUTIVE;
      else if (award.name === 'Diamond Club') fileName = this.ICON_CLUB_DIAMOND;
      // else if (award.name === 'President\'s Club') fileName = this.;
    } else if (isTeamAward) {
      if (award.name === 'Executive Club Team') fileName = this.ICON_TEAM_EXECUTIVE;
      else if (award.name === 'Platinum Club Team') fileName = this.ICON_TEAM_PLATINUM;
      else if (award.name === 'Diamond Club Team') fileName = this.ICON_TEAM_DIAMOND;
      else if (award.name === 'Chairman\'s Club Team') fileName = this.ICON_TEAM_CHAIRMAN;
      else if (award.name === 'Titan Club Team') fileName = this.ICON_TEAM_TITAN;
      else if (award.name === '100% Club Team') fileName = this.ICON_TEAM_100_PERCENT;
    }
    else {
      if (award.name === 'Lifetime Achievement') fileName = this.ICON_CAREER_2017_LIFETIME_ACHIEVEMENT;
      else if (award.name === 'Hall Of Fame') fileName = this.ICON_CAREER_2017_HALL_OF_FAME;
      else if (award.name === 'Circle Of Legends') fileName = this.ICON_CAREER_2017_CIRCLE_LEGENDS;
      else if (award.name === 'Luminary Of Distinction') fileName = this.ICON_CAREER_2017_LUMINARY_DISTINCTION;
    }
    return environment.imageBucket + fileName;
  }

  static trackBy(index, agent: Agent) {
    return agent.personifyId;
  }

  private _groupSameAwards(awards: Array<AgentAward>, isYearlyAward: boolean, isTeamAward: boolean): Array<AgentAward> {
    const awardSets = Array<AgentAward>();
    // We want to group awards by sequential years for the same award
    let prevAward: AgentAward = null;

    awards.forEach((award) => {
      if (award.name === 'President\'s Club') return;
      // When we first start iterating
      if (!prevAward) {
        prevAward = {
          years: [award.year],
          year: award.year,
          url: this._getAwardIcon(award, isYearlyAward, isTeamAward),
          name: award.name
        };
      }
      // Continued streak of same award given
      else if (prevAward.year === award.year + 1 && prevAward.name === award.name) {
        // Add the continued streak of years and update the last year to the current award year
        prevAward.years.push(award.year);
        prevAward.year = award.year;
      }
      // Current award and the previous award are not sequential or different awards
      else {
        // Add the streak of years to the full set, and reset the previous award
        awardSets.push(prevAward);
        prevAward = {
          years: [award.year],
          year: award.year,
          url: this._getAwardIcon(award, isYearlyAward, isTeamAward),
          name: award.name
        };
      }
    });
    // If end up here and prevAward exists, make sure to include that as well
    if (prevAward) awardSets.push(prevAward);

    return awardSets;
  }
}

export class AgentFilters {
  realEstateTypes: Array<any>;
  languages: Array<string>;
  specialties: Array<string>;
  proudSupporter: Array<string>;
}

export class AgentConfig {
  awardYearsToHideOA: Array<Number>;
  awardSeriesToHideOA: Array<string>;
  awardYearsToHideWC: Array<Number>;
  awardSeriesToHideWC: Array<string>;
  hideAllAwardsOA: boolean;
  hideAllAwardsWC: boolean;
}
